import * as React from 'react'
import { cn } from '@/lib/utils'

const ButtonSwitch = React.forwardRef(({
  className,
  leftOption = "Option 1",
  rightOption = "Option 2",
  value,
  onValueChange,
  disabled = false,
  ...props
}, ref) => {
  const handleClick = () => {
    if (!disabled && onValueChange) {
      onValueChange(!value)
    }
  }

  // Measure each option separately
  const [leftWidth, setLeftWidth] = React.useState(0)
  const [rightWidth, setRightWidth] = React.useState(0)
  const leftRef = React.useRef(null)
  const rightRef = React.useRef(null)

  React.useEffect(() => {
    if (leftRef.current && rightRef.current) {
      setLeftWidth(leftRef.current.offsetWidth)
      setRightWidth(rightRef.current.offsetWidth)
    }
  }, [leftOption, rightOption])

  // Add 32px padding (16px per side) to each option
  const paddedLeft = leftWidth + 32
  const paddedRight = rightWidth + 32
  const maxPadded = Math.max(paddedLeft, paddedRight)
  const totalWidth = maxPadded * 2 + 8 // 4px padding on each side

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex items-center h-[50px] rounded-[12px] bg-[var(--White---Hover,#F0F0F0)] p-1 flex-shrink flex-grow',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      style={{
        minWidth: Math.max(totalWidth, 120),
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={handleClick}
      {...props}
    >
      {/* Hidden measurement spans */}
      <span
        ref={leftRef}
        className="absolute opacity-0 pointer-events-none whitespace-nowrap font-medium text-[14px]"
        style={{
          padding: '0 16px',
          fontWeight: 500,
          fontSize: 14,
          lineHeight: '21px',
        }}
      >
        {leftOption}
      </span>
      <span
        ref={rightRef}
        className="absolute opacity-0 pointer-events-none whitespace-nowrap font-medium text-[14px]"
        style={{
          padding: '0 16px',
          fontWeight: 500,
          fontSize: 14,
          lineHeight: '21px',
        }}
      >
        {rightOption}
      </span>
      {/* Sliding Rectangle */}
      <div
        className={cn(
          'absolute top-1 h-[42px] rounded-[8px] bg-white',
        )}
        style={{
          width: 'calc(50% - 2px)',
          left: value ? 'calc(50% - 2px)' : '4px',
          zIndex: 1,
          transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      />
      {/* Options */}
      <div className="relative flex w-full z-10">
        <div
          className="flex items-center justify-center flex-1"
          style={{
            color: 'black',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: '21px',
            zIndex: 2,
            userSelect: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {leftOption}
        </div>
        <div
          className="flex items-center justify-center flex-1"
          style={{
            color: 'black',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: '21px',
            zIndex: 2,
            userSelect: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {rightOption}
        </div>
      </div>
    </div>
  )
})

ButtonSwitch.displayName = 'ButtonSwitch'

export { ButtonSwitch } 